import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  LayoutDashboard,
  Users,
  MapPin,
  Settings,
  FileText,
  TreePine,
  Recycle,
  Heart,
  GraduationCap,
  Dumbbell,
  Leaf,
  DollarSign,
  Navigation,
  Image,
  Video,
  Folder,
  ImageIcon,
  Radio,
  BarChart3,
  UserPlus,
  Flower2,
  ChevronRight,
  Database,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { hasReviewAccessForCodes } from '@/features/impact-data/utils/programResource';

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<any>;
  /** Any-of role list. When set, the user must hold at least one of these
   *  roles verbatim to see the item. `ADMIN` is always allowed. */
  requiredRoles?: string[];
  /**
   * Program-code prefixes. A user sees the item when they hold *any* role
   *  prefixed with `<code>.` for one of these codes. Encodes the "which
   *  program is this user assigned to" question the JWT answers via
   *  `PROGRAM.SUBROLE` style role strings. `ADMIN` is always allowed.
   *  When both `requiredRoles` and `programCodes` are set, either match
   *  grants access.
   */
  programCodes?: string[];
  children?: NavigationItem[];
  /**
   * Nested items that live *under* this item as its own expandable panel
   * (as opposed to `children`, which renders this item as a section
   * header). Set on leaf items — e.g. Programs use it to expose the
   * "Impact Data" entry that appears on click or hover.
   */
  submenu?: NavigationItem[];
}

const navigation: NavigationItem[] = [
  {
    name: 'Dashboard',
    href: '/dashboard',
    icon: LayoutDashboard,
  },
  {
    name: 'User Management',
    href: '#',
    icon: Users,
    // Temporarily removed ADMIN requirement for testing
    // requiredRoles: ['ADMIN'],
    children: [
      {
        name: 'Users',
        href: '/users',
        icon: Users,
      },
      {
        name: 'Onboarding',
        href: '/onboarding/candidates',
        icon: UserPlus,
        requiredRoles: ['ADMIN', 'ONBOARDING.PROGRAM_ASSOCIATE', 'ONBOARDING.PNC_OFFICER','COLLABORATION_RENEW.FINANCE'],
      },
      {
        name: 'Honorarium Management',
        href: '/honorarium',
        icon: DollarSign,
        // requiredRoles: ['ADMIN', 'HR.LEAD'],
      },
    ],
  },
  {
    name: 'Location Tracking',
    href: '#',
    icon: Navigation,
    requiredRoles: ['ADMIN', 'LOCATION.TRACKING_VIEWER', 'LOCATION.REPORT_VIEWER', 'LOCATION.USER_MANAGER'],
    children: [
      {
        name: 'Live Tracking',
        href: '/location-tracking/live',
        icon: Radio,
        requiredRoles: ['ADMIN', 'LOCATION.TRACKING_VIEWER'],
      },
      {
        name: 'Reports',
        href: '/location-tracking/reports',
        icon: BarChart3,
        requiredRoles: ['ADMIN', 'LOCATION.REPORT_VIEWER'],
      },
      {
        name: 'User Management',
        href: '/location-tracking/users',
        icon: Users,
        requiredRoles: ['ADMIN', 'LOCATION.USER_MANAGER'],
      },
    ],
  },
  {
    name: 'Asset Management',
    href: '#',
    icon: Image,
    requiredRoles: ['ADMIN', 'IMPACT_ASSET.VIEWER', 'PHOTO_TEAM', 'MARKETING', 'GALLERY.VIEWER', 'GALLERY.MODERATOR'],
    children: [
      {
        name: 'Photo Sources',
        href: '/asset-management/photo-sources',
        icon: Image,
        requiredRoles: ['ADMIN', 'IMPACT_ASSET.VIEWER', 'PHOTO_TEAM', 'MARKETING'],
      },
      {
        name: 'Video Sources',
        href: '/asset-management/video-sources',
        icon: Video,
        requiredRoles: ['ADMIN', 'IMPACT_ASSET.VIEWER', 'VIDEO_TEAM', 'MARKETING'],
      },
      {
        name: 'Asset Library',
        href: '/asset-management/asset-library',
        icon: Folder,
        requiredRoles: ['ADMIN', 'IMPACT_ASSET.VIEWER', 'MARKETING'],
      },
      {
        name: 'Visual Galleries',
        href: '/asset-management/galleries',
        icon: ImageIcon,
        requiredRoles: ['ADMIN', 'GALLERY.VIEWER', 'GALLERY.MODERATOR'],
      },
    ],
  },
  {
    name: 'Programs',
    href: '#',
    icon: TreePine,
    // Each entry shows when the user holds any role prefixed with one of
    // the listed program codes (e.g. TBTP.CHANGE_MAKER, TBTP.COORDINATOR).
    // Kept as an array because Sports covers the RGS sub-brands too.
    children: [
      {
        name: 'TBTP',
        href: '/programs/tbtp',
        icon: TreePine,
        programCodes: ['TBTP'],
        submenu: [
          { name: 'Impact Data', href: '/programs/tbtp/impact-data', icon: Database },
        ],
      },
      {
        name: 'Waste Management',
        href: '/programs/waste-management',
        icon: Recycle,
        programCodes: ['WM'],
        submenu: [
          { name: 'Impact Data', href: '/programs/waste-management/impact-data', icon: Database },
        ],
      },
      {
        name: 'Women Equity',
        href: '/programs/women-equity',
        icon: Heart,
        programCodes: ['WE'],
        submenu: [
          { name: 'Impact Data', href: '/programs/women-equity/impact-data', icon: Database },
        ],
      },
      {
        name: 'Learn Labs',
        href: '/programs/learn-labs',
        icon: GraduationCap,
        programCodes: ['LL'],
        submenu: [
          { name: 'Impact Data', href: '/programs/learn-labs/impact-data', icon: Database },
        ],
      },
      {
        name: 'Sports',
        href: '/programs/sports',
        icon: Dumbbell,
        // Sports covers the original SD program plus the RGS sub-brands
        // (RGS Hockey / Football / Tennis / Golf). Any role prefixed with
        // one of these grants access.
        programCodes: ['SD'],
        submenu: [
          { name: 'Impact Data', href: '/programs/sports/impact-data', icon: Database },
        ],
      },
      {
        name: 'Regenerative Agriculture',
        href: '/programs/regenerative-agriculture',
        icon: Leaf,
        programCodes: ['RF'],
        submenu: [
          { name: 'Impact Data', href: '/programs/regenerative-agriculture/impact-data', icon: Database },
        ],
      },
      {
        name: 'Yoga',
        href: '/programs/yoga',
        icon: Flower2,
        programCodes: ['YOGA'],
        submenu: [
          { name: 'Impact Data', href: '/programs/yoga/impact-data', icon: Database },
        ],
      },
    ],
  },
  {
    name: 'Location Management',
    href: '/locations',
    icon: MapPin,
    requiredRoles: ['ADMIN'],
  },
  {
    name: 'Audit Logs',
    href: '/audit',
    icon: FileText,
    requiredRoles: ['ADMIN'],
  },
  {
    name: 'Settings',
    href: '/settings',
    icon: Settings,
    requiredRoles: ['ADMIN'],
  },
];

/**
 * Depth → indent class. Depth 0 has no indent; depth 1 (children of a
 * section header, e.g. TBTP under "Programs") gets ml-4; depth 2 (a
 * program's own sub-item, e.g. Impact Data under TBTP) gets ml-8.
 * Tailwind's JIT can't parse computed `ml-${n*4}` strings, so this is a
 * static lookup.
 */
const indentClassForDepth = (depth: number): string => {
  if (depth <= 0) return '';
  if (depth === 1) return 'ml-4';
  return 'ml-8';
};

/**
 * Expandable leaf: the program row itself. Click toggles the sub-menu
 * open/closed. Hover does *not* open — accidental cursor drift over
 * the sidebar shouldn't rearrange the layout. The chevron rotates 90°
 * when open so the affordance stays visible.
 *
 * The header is a `<button>` (not a `<NavLink>`) because none of the
 * programs currently have a landing page — clicking the row's only job
 * is to reveal the sub-menu. When landing pages ship, split this into
 * a NavLink label + a chevron-only toggle button.
 */
const ExpandableNavItem: React.FC<{
  item: NavigationItem;
  depth: number;
  visibleSubmenu: NavigationItem[];
}> = ({ item, depth, visibleSubmenu }) => {
  const [open, setOpen] = useState(false);
  const Icon = item.icon;
  const headerIndent = indentClassForDepth(depth);
  const subIndent = indentClassForDepth(depth + 1);

  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={clsx(
          'group flex w-full items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors duration-200 text-rgf-gray-600 hover:bg-rgf-gray-50 hover:text-rgf-gray-900',
          headerIndent
        )}
      >
        <Icon className="mr-3 h-5 w-5 flex-shrink-0" />
        <span className="flex-1 text-left">{item.name}</span>
        <ChevronRight
          className={clsx(
            'h-4 w-4 flex-shrink-0 transition-transform duration-200',
            open && 'rotate-90'
          )}
        />
      </button>
      {open &&
        visibleSubmenu.map((sub) => {
          const SubIcon = sub.icon;
          return (
            <NavLink
              key={sub.href}
              to={sub.href}
              className={({ isActive }) =>
                clsx(
                  'group flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors duration-200',
                  subIndent,
                  isActive
                    ? 'bg-rgf-primary-50 text-rgf-primary-700 border-r-2 border-rgf-primary-500'
                    : 'text-rgf-gray-600 hover:bg-rgf-gray-50 hover:text-rgf-gray-900'
                )
              }
            >
              <SubIcon className="mr-3 h-5 w-5 flex-shrink-0" />
              {sub.name}
            </NavLink>
          );
        })}
    </div>
  );
};

interface SidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ sidebarOpen }) => {
  const { user } = useAuth();

  /**
   * Access check for a sidebar item. Two independent grants — either
   * one is enough:
   *
   *   1. `requiredRoles` — any-of match on the exact role strings.
   *   2. `programCodes` — the user holds a `RESOUCE_REVIEW.<code>`
   *      role for at least one of the listed program codes. This is
   *      the reviewer-access model the backend authoritatively uses
   *      for the review workflow (see `hasReviewAccessForCodes`).
   *
   * `ADMIN` is always allowed. When neither list is set the item has no
   * gate and is shown to every authenticated user.
   */
  const hasAccess = (item: NavigationItem): boolean => {
    const requiredRoles = item.requiredRoles;
    const programCodes = item.programCodes;
    const hasAnyGate =
      (requiredRoles && requiredRoles.length > 0) ||
      (programCodes && programCodes.length > 0);
    if (!hasAnyGate) return true;

    const roles = user?.roles;
    if (!Array.isArray(roles) || roles.length === 0) return false;
    if (roles.includes('ADMIN')) return true;

    if (requiredRoles && requiredRoles.some((r) => roles.includes(r))) return true;
    if (programCodes && hasReviewAccessForCodes(programCodes, roles)) return true;
    return false;
  };

  const renderNavigationItem = (item: NavigationItem, depth = 0) => {
    if (!hasAccess(item)) return null;

    if (item.children) {
      const accessibleChildren = item.children.filter((child) => hasAccess(child));
      if (accessibleChildren.length === 0) return null;

      return (
        <div key={item.name} className="space-y-1">
          <div className={clsx(
            'px-4 py-2 text-sm font-medium text-rgf-gray-500 uppercase tracking-wider',
            indentClassForDepth(depth)
          )}>
            {item.name}
          </div>
          {accessibleChildren.map(child => renderNavigationItem(child, depth + 1))}
        </div>
      );
    }

    if (item.submenu && item.submenu.length > 0) {
      const visibleSubmenu = item.submenu.filter((sub) => hasAccess(sub));
      return (
        <ExpandableNavItem
          key={item.name}
          item={item}
          depth={depth}
          visibleSubmenu={visibleSubmenu}
        />
      );
    }

    const Icon = item.icon;

    return (
      <NavLink
        key={item.href}
        to={item.href}
        className={({ isActive }) => clsx(
          'group flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors duration-200',
          indentClassForDepth(depth),
          isActive
            ? 'bg-rgf-primary-50 text-rgf-primary-700 border-r-2 border-rgf-primary-500'
            : 'text-rgf-gray-600 hover:bg-rgf-gray-50 hover:text-rgf-gray-900'
        )}
      >
        <Icon className="mr-3 h-5 w-5 flex-shrink-0" />
        {item.name}
      </NavLink>
    );
  };

  return (
    <div
      className={clsx(
        // `flex flex-col` is what makes the inner `overflow-y-auto` nav
        // area actually scroll instead of overflowing the viewport.
        // Without it the nav has no bounded height and the user tile
        // slides off-screen for accounts with lots of program access.
        'fixed inset-y-0 left-0 z-50 flex w-64 flex-col bg-white shadow-lg border-r border-rgf-gray-200 transform transition-transform duration-300 ease-in-out',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      )}
    >
      {/* Navigation */}
      <nav className="mt-4 px-3 space-y-1 flex-1 min-h-0 overflow-y-auto scrollbar-thin">
        {navigation.map(item => renderNavigationItem(item))}
      </nav>

      {/* User info */}
      <div className="border-t border-rgf-gray-200 p-4 flex-shrink-0">
        <div className="flex items-center">
          <div className="h-8 w-8 bg-rgf-primary-100 rounded-full flex items-center justify-center">
            <span className="text-sm font-medium text-rgf-primary-700">
              {user?.email?.[0]?.toUpperCase() || 'U'}
            </span>
          </div>
          <div className="ml-3">
            <p className="text-sm font-medium text-rgf-gray-900">
              {user?.first_name} {user?.last_name}
            </p>
            <p className="text-xs text-rgf-gray-500">{user?.email}</p>
          </div>
        </div>
      </div>
    </div>
  );
};